import React, {Suspense, useEffect} from "react";
import {BrowserRouter, Routes, Route} from "react-router-dom";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {HelmetProvider} from "react-helmet-async";
import {ClerkProvider, useUser} from "@clerk/clerk-react";
import "./global.css";
import "./../node_modules/swiper/swiper-bundle.css";
import Layout from "./components/Layout";
import {I18nextProvider} from "react-i18next";
import i18n from "./i18n";
import {ErrorBoundary} from "./components/ErrorBoundary";
import {Preloader} from "./components/decorative/Preloader";
import {useSelector, useDispatch} from "react-redux";
import {setUser, logout} from "./store/authSlice";

// ── React Query client ────────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ── Lazy-loaded routes ────────────────────────────────────────────────────────
const HomePage         = React.lazy(() => import("./pages/HomePage").then(m => ({default: m.HomePage})));
const DiscoverPage     = React.lazy(() => import("./pages/DiscoverPage").then(m => ({default: m.DiscoverPage})));
const BrowsePage       = React.lazy(() => import("./pages/BrowsePage").then(m => ({default: m.BrowsePage})));
const ContentPage      = React.lazy(() => import("./pages/ContentPage").then(m => ({default: m.ContentPage})));
const CastPage         = React.lazy(() => import("./pages/CastPage").then(m => ({default: m.CastPage})));
const SeasonsPage      = React.lazy(() => import("./pages/SeasonsPage").then(m => ({default: m.SeasonsPage})));
const EpisodesPage     = React.lazy(() => import("./pages/EpisodesPage").then(m => ({default: m.EpisodesPage})));
const PersonPage       = React.lazy(() => import("./pages/PersonPage").then(m => ({default: m.PersonPage})));
const ProfilePage      = React.lazy(() => import("./pages/ProfilePage").then(m => ({default: m.ProfilePage})));
const LoginPage        = React.lazy(() => import("./pages/LoginPage").then(m => ({default: m.LoginPage})));
const SettingsPage     = React.lazy(() => import("./pages/SettingsPage").then(m => ({default: m.SettingsPage})));
const FavoriteWatchlistPage = React.lazy(() => import("./pages/FavoriteWatchlistPage").then(m => ({default: m.FavoriteWatchlistPage})));
const AboutPage        = React.lazy(() => import("./pages/AboutPage").then(m => ({default: m.AboutPage})));
const CollectionPage   = React.lazy(() => import("./pages/CollectionPage").then(m => ({default: m.CollectionPage})));
const RecommendationsPage = React.lazy(() => import("./pages/RecommendationsPage").then(m => ({default: m.RecommendationsPage})));
const SwipePage        = React.lazy(() => import("./pages/SwipePage").then(m => ({default: m.SwipePage})));
const SearchPage       = React.lazy(() => import("./pages/SearchPage").then(m => ({default: m.SearchPage})));
const AnimePage        = React.lazy(() => import("./pages/AnimePage").then(m => ({default: m.AnimePage})));
const JikanDetailPage     = React.lazy(() => import("./pages/JikanDetailPage").then(m => ({default: m.JikanDetailPage})));
const JikanCharacterPage  = React.lazy(() => import("./pages/JikanCharacterPage").then(m => ({default: m.JikanCharacterPage})));
const NotFoundPage     = React.lazy(() => import("./pages/NotFoundPage").then(m => ({default: m.NotFoundPage})));

// ── Page fallback ─────────────────────────────────────────────────────────────
const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <Preloader />
  </div>
);

// ── Theme applier ─────────────────────────────────────────────────────────────
const ThemeApplier = () => {
  const theme = useSelector((state) => state.settings?.theme ?? "dark");
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);
  return null;
};

// ── Clerk → Redux auth sync ───────────────────────────────────────────────────
const ClerkAuthSync = () => {
  const {user, isLoaded} = useUser();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isLoaded) return;
    if (user) {
      dispatch(setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress ?? null,
        name: user.fullName ?? user.username ?? user.primaryEmailAddress?.emailAddress?.split("@")[0] ?? null,
        avatarUrl: user.imageUrl ?? null,
      }));
    } else {
      dispatch(logout());
    }
  }, [user, isLoaded, dispatch]);

  return null;
};

const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

function App() {
  return (
    <ClerkProvider publishableKey={CLERK_KEY}>
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter>
          <ThemeApplier />
          <ClerkAuthSync />
          <Layout>
            <ErrorBoundary>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/movflix/" element={<DiscoverPage />} />
                  <Route path="/movflix/catalogue" element={<DiscoverPage />} />
                  <Route path="/movflix/discover/*" element={<BrowsePage />} />
                  <Route path="/movflix/page" element={<ContentPage />} />
                  <Route path="/movflix/cast_crew" element={<CastPage />} />
                  <Route path="/movflix/seasons" element={<SeasonsPage />} />
                  <Route path="/movflix/episodes" element={<EpisodesPage />} />
                  <Route path="/movflix/person" element={<PersonPage />} />
                  <Route path="/movflix/profile" element={<ProfilePage />} />
                  <Route path="/movflix/login" element={<LoginPage />} />
                  <Route path="/movflix/settings" element={<SettingsPage />} />
                  <Route path="/movflix/lists" element={<FavoriteWatchlistPage />} />
                  <Route path="/movflix/about" element={<AboutPage />} />
                  <Route path="/movflix/collection" element={<CollectionPage />} />
                  <Route path="/movflix/recommendations" element={<RecommendationsPage />} />
                  <Route path="/movflix/swipe" element={<SwipePage />} />
                  <Route path="/movflix/search" element={<SearchPage />} />
                  <Route path="/movflix/anime" element={<AnimePage />} />
                  <Route path="/movflix/anime/detail" element={<JikanDetailPage />} />
                  <Route path="/movflix/anime/character" element={<JikanCharacterPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Layout>
        </BrowserRouter>
      </I18nextProvider>
    </QueryClientProvider>
    </HelmetProvider>
    </ClerkProvider>
  );
}

export default App;
